import React from "react";
import { Sun, Calendar, Clock, Users, MapPin, MessageCircle, ArrowRight, Sparkles } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { trackSummerCTA } from "../lib/analytics";

const WA = "https://wa.me/524421182816?text=" +
  encodeURIComponent("Hola, quiero información del Summer Camp 2027 en Steps");

const PHOTOS = [
  "/images/summer2.jpeg",
  "/images/summer5.jpeg",
  "/images/summer13.jpeg",
  "/images/summer1.jpeg",
];

const SummerCamp = () => {
  const { t } = useLang();

  const infoItems = [
    { icon: Calendar, label: t.summer.info.dates.label, value: t.summer.info.dates.value },
    { icon: Clock, label: t.summer.info.hours.label, value: t.summer.info.hours.value },
    { icon: Users, label: t.summer.info.ages.label, value: t.summer.info.ages.value },
    { icon: MapPin, label: t.summer.info.location.label, value: t.summer.info.location.value },
  ];

  return (
    <section
      id="summer-camp"
      data-testid="summer-camp-section"
      className="relative overflow-hidden py-20 md:py-28 bg-gradient-to-br from-[#2E9E4E] via-[#3FB55E] to-[#FF8A33]"
    >
      {/* decorative blobs */}
      <div className="blob bg-[#FFD500] w-[380px] h-[380px] -top-20 -right-20 opacity-40" />
      <div className="blob bg-[#FF6E5F] w-[440px] h-[440px] -bottom-40 -left-20 opacity-40" />
      <div className="blob bg-white w-[260px] h-[260px] top-1/3 right-1/3 opacity-10" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Header block */}
        <div className="text-center mb-14 md:mb-16">
          <div className="inline-flex items-center gap-2 rounded-full bg-[#FFD500] text-[#1D3557] px-5 py-2 mb-6 font-bold text-xs md:text-sm uppercase tracking-widest shadow-lg">
            <Sun className="w-4 h-4" strokeWidth={2.5} />
            {t.summer.overline}
          </div>
          <h2 className="font-fredoka text-4xl sm:text-5xl lg:text-6xl text-white leading-none mb-4 drop-shadow-lg">
            <span className="text-white">Summer </span>
            <span className="text-[#FFD500]">Camp</span>
            <span className="ml-3 inline-block bg-[#FFD500] text-[#1D3557] rounded-2xl px-4 py-1 text-2xl sm:text-3xl lg:text-4xl align-middle shadow-lg">{t.summer.year}</span>
          </h2>
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur border border-white/30 px-4 py-1.5 mb-4 text-xs md:text-sm font-semibold text-white">
            ✓ {t.summer.concludedBadge}
          </div>
          <p className="text-white/95 max-w-2xl mx-auto text-base md:text-lg drop-shadow">
            {t.summer.subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Photo collage 2x2 */}
          <div className="lg:col-span-7 grid grid-cols-2 gap-3 md:gap-4">
            {PHOTOS.map((src, i) => (
              <div
                key={src}
                data-testid={`summer-photo-${i}`}
                className={`relative rounded-2xl overflow-hidden border-4 border-white shadow-2xl aspect-[4/3] hover:scale-[1.02] transition-transform`}
              >
                <img src={src} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>

          {/* Info & CTA panel */}
          <div className="lg:col-span-5">
            <div className="bg-white/15 backdrop-blur-md rounded-3xl p-6 md:p-8 border-2 border-white/25 shadow-2xl">
              <div className="grid grid-cols-2 gap-4 md:gap-5">
                {infoItems.map(({ icon: Icon, label, value }) => (
                  <div key={label} data-testid={`summer-info-${label}`} className="flex flex-col">
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-8 h-8 rounded-full bg-[#FFD500] flex items-center justify-center shrink-0 shadow">
                        <Icon className="w-4 h-4 text-[#1D3557]" strokeWidth={2.5} />
                      </div>
                      <span className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-[#FFD500] drop-shadow">
                        {label}
                      </span>
                    </div>
                    <span className="font-fredoka text-white text-lg md:text-xl leading-tight drop-shadow">
                      {value}
                    </span>
                  </div>
                ))}
              </div>

              {/* activities chips */}
              <div className="mt-6 pt-6 border-t border-white/25">
                <p className="text-[10px] md:text-xs font-bold uppercase tracking-wider text-[#FFD500] mb-3 drop-shadow">
                  {t.summer.activitiesLabel}
                </p>
                <div className="flex flex-wrap gap-2">
                  {t.summer.activities.map((a) => (
                    <span
                      key={a}
                      data-testid={`summer-chip-${a}`}
                      className="bg-[#FFD500] text-[#1D3557] rounded-full px-3 py-1 text-xs md:text-sm font-semibold shadow"
                    >
                      {a}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* CTAs */}
            <div className="mt-6 space-y-3">
              <a
                data-testid="summer-cta-whatsapp"
                href={WA}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackSummerCTA("whatsapp")}
                className="w-full inline-flex items-center justify-center gap-2 bg-[#FFD500] hover:bg-white text-[#1D3557] font-bold rounded-full px-6 py-4 text-base md:text-lg transition-all hover:scale-[1.02] shadow-xl"
              >
                <MessageCircle className="w-5 h-5" strokeWidth={2.5} />
                {t.summer.ctaWhatsapp}
              </a>
              <a
                data-testid="summer-cta-form"
                href="#contacto"
                onClick={() => trackSummerCTA("form")}
                className="w-full inline-flex items-center justify-center gap-2 bg-white/20 hover:bg-white/30 text-white font-semibold rounded-full px-6 py-4 text-sm md:text-base border-2 border-white/40 transition-colors backdrop-blur"
              >
                <Sparkles className="w-4 h-4" strokeWidth={2.5} />
                {t.summer.ctaForm}
                <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
              </a>
            </div>

            <p className="mt-4 text-center text-[#FFD500] font-bold text-sm md:text-base drop-shadow-lg">
              {t.summer.urgency}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default SummerCamp;
